/**
 * PublicRegisterPage — /l/:slug/register and /l/:slug/register/:divisionSlug
 * ─────────────────────────────────────────────────────────────────
 * v2.5.134 — Phase 9B
 *
 * The public-facing registration form. Anonymous users can browse
 * the form contents (config + fields + waivers). Submit requires
 * a logged-in Game Day account (Q3 answer).
 *
 * Sections of the page:
 *   1. Header with league name + season + (optional) division name
 *   2. Intro (manager-edited markdown)
 *   3. "Who are you registering?" — search existing player by UID
 *      or name, OR create a new profile
 *   4. Form fields (dynamically rendered from the manager's config)
 *   5. Waiver sections (per-section markdown + checkbox)
 *   6. Typed-name signature (Q3: one signature applies to all
 *      acknowledged sections)
 *   7. Submit button
 *
 * Server-side draft autosave (Q4): every field change schedules
 * a debounced PUT to /api/registration/:configId/draft. The draft
 * resumes from any device when the user comes back.
 *
 * Account gating: the config + form are visible anonymously, but
 * any action that writes (autosave, submit) requires auth. We
 * surface a "Sign in to start" prompt on first interaction if
 * not signed in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Search, UserPlus, CheckCircle2, AlertCircle, LogIn, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import publicApi from '../../services/publicApi.js';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { renderMarkdown } from '../../utils/tinyMarkdown.jsx';

export default function PublicRegisterPage() {
  const { slug, divisionSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Server-loaded config + fields + waivers + draft.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [bundle, setBundle] = useState(null); // { league, season, division, config, fields, waiver_sections }
  const [draft, setDraft] = useState(null);

  // Working state. Initialized from draft once loaded.
  const [registrantMode, setRegistrantMode] = useState(null); // 'new' | 'existing_by_uid' | 'existing_by_name'
  const [selectedProfile, setSelectedProfile] = useState(null); // { id, player_uid, display_name, ... }
  const [newProfile, setNewProfile] = useState({ display_name: '', first_name: '', last_name: '', date_of_birth: '' });
  const [relationship, setRelationship] = useState('parent');
  const [fieldValues, setFieldValues] = useState({}); // { [fieldId]: value }
  const [ackedSectionIds, setAckedSectionIds] = useState(new Set());
  const [signatureText, setSignatureText] = useState('');

  // Load config (anonymous).
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError(null);
    const url = divisionSlug
      ? `/leagues/${encodeURIComponent(slug)}/register/${encodeURIComponent(divisionSlug)}`
      : `/leagues/${encodeURIComponent(slug)}/register`;
    publicApi.get(url)
      .then(({ data }) => { if (!cancelled) setBundle(data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setLoadError(divisionSlug ? 'Registration not found for this division' : 'Registration not currently available');
        } else {
          setLoadError('Failed to load registration');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, divisionSlug]);

  // Phase C — return-from-Stripe banner (?payment=success|cancelled)
  // and the user's existing non-draft registrations on this config
  // (so someone coming back later still sees their Pay button or
  // their paid/confirmed status).
  const paymentParam = new URLSearchParams(location.search).get('payment');
  const [myRegs, setMyRegs] = useState([]);
  const [regsRefreshKey, setRegsRefreshKey] = useState(0);
  useEffect(() => {
    if (!bundle?.config?.id || !user) return;
    let cancelled = false;
    api.get(`/registration/${bundle.config.id}/my-registrations`)
      .then(({ data }) => { if (!cancelled) setMyRegs(data?.registrations || []); })
      .catch(() => { /* quiet — banner simply doesn't render */ });
    return () => { cancelled = true; };
  }, [bundle?.config?.id, user, regsRefreshKey]);
  // After a successful Stripe return, the webhook usually lands
  // within seconds — refetch a couple of times so the status flips
  // from awaiting_payment to paid without a manual reload.
  useEffect(() => {
    if (paymentParam !== 'success') return;
    const t1 = setTimeout(() => setRegsRefreshKey(k => k + 1), 4000);
    const t2 = setTimeout(() => setRegsRefreshKey(k => k + 1), 12000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [paymentParam]);

  // Load existing draft once we have a config AND we're authenticated.
  useEffect(() => {
    if (!bundle?.config?.id || !user) return;
    let cancelled = false;
    api.get(`/registration/${bundle.config.id}/draft`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!data.draft) return;
        setDraft(data.draft);
        if (data.draft.profile_match_mode) setRegistrantMode(data.draft.profile_match_mode);
        if (data.draft.submitting_user_relationship) setRelationship(data.draft.submitting_user_relationship);
        if (data.draft.field_values) setFieldValues(data.draft.field_values);
        if (Array.isArray(data.draft.acknowledged_section_ids)) {
          setAckedSectionIds(new Set(data.draft.acknowledged_section_ids));
        }
        // If draft already has a player_profile_id, fetch the
        // profile details so the parent sees who they were
        // registering. Done as a separate search by UID once
        // available; for 9B we just preserve the ID and surface
        // a generic "Resuming registration" notice.
      })
      .catch((err) => {
        if (err?.response?.status === 404) return; // no draft yet — fine
        console.error('draft load error:', err);
      });
    return () => { cancelled = true; };
  }, [bundle?.config?.id, user]);

  // Debounced autosave on any change. Only runs if we're signed in
  // and the bundle is loaded.
  const autosaveTimer = useRef(null);
  const lastAutosavePayload = useRef('');
  useEffect(() => {
    if (!bundle?.config?.id || !user) return;
    const payload = {
      registrant_player_profile_id: selectedProfile?.id || null,
      profile_match_mode: registrantMode,
      submitting_user_relationship: relationship,
      field_values: fieldValues,
    };
    // Cheap idempotence guard so the autosave doesn't refire when
    // nothing material has changed.
    const sig = JSON.stringify(payload);
    if (sig === lastAutosavePayload.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      lastAutosavePayload.current = sig;
      api.put(`/registration/${bundle.config.id}/draft`, payload)
        .catch((err) => {
          // Silent — autosave shouldn't disturb the user. Real
          // submit failures surface via the submit button.
          console.warn('autosave failed:', err?.response?.data?.error || err.message);
        });
    }, 1200);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [bundle?.config?.id, user, selectedProfile?.id, registrantMode, relationship, fieldValues]);

  function toggleAck(sectionId) {
    setAckedSectionIds(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  function setFieldValue(fieldId, value) {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  }

  // Submit handler.
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  async function submit() {
    if (!user) {
      toast.error('Please sign in to submit your registration');
      return;
    }
    if (!bundle?.config?.id) return;

    // Client-side preflight to give nice feedback before the round-trip.
    // The server is the source of truth; this is just UX.
    if (!selectedProfile && registrantMode !== 'new') {
      toast.error('Pick or create a player');
      return;
    }
    if (registrantMode === 'new' && !newProfile.display_name.trim()) {
      toast.error("Enter the player's name");
      return;
    }
    for (const f of bundle.fields) {
      if (!f.is_required) continue;
      const v = fieldValues[f.id];
      const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const requiredAcks = bundle.waiver_sections.filter(s => s.is_required).map(s => s.id);
    for (const id of requiredAcks) {
      if (!ackedSectionIds.has(id)) {
        const s = bundle.waiver_sections.find(x => x.id === id);
        toast.error(`Please acknowledge "${s?.title || 'waiver'}"`);
        return;
      }
    }
    if (signatureText.trim().length < 2) {
      toast.error('Type your full name as the signature');
      return;
    }

    // Build the submit body.
    const body = {
      signature_text: signatureText.trim(),
      waiver_acknowledgments: Array.from(ackedSectionIds).map(id => ({ waiver_section_id: id })),
    };
    if (registrantMode === 'new') {
      body.create_new_profile = {
        display_name: newProfile.display_name.trim(),
        first_name: newProfile.first_name.trim() || null,
        last_name: newProfile.last_name.trim() || null,
        date_of_birth: newProfile.date_of_birth || null,
      };
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`/registration/${bundle.config.id}/submit`, body);
      setSubmitResult(data.registration);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────
  if (loading || authLoading) {
    return <div style={containerStyle}><div style={cardStyle}>Loading…</div></div>;
  }
  if (loadError) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Link to={`/l/${slug}`} style={backLinkStyle}><ArrowLeft size={14} /> Back</Link>
          <h1 style={h1Style}>Not available</h1>
          <p style={{ color: '#7a82a0' }}>{loadError}</p>
        </div>
      </div>
    );
  }
  if (!bundle) return null;

  if (submitResult) {
    return <SubmittedScreen registration={submitResult} bundle={bundle} slug={slug} />;
  }

  // Opens/closes window display. Server enforces on submit; we
  // surface it pre-submit so the parent knows what to expect.
  const now = new Date();
  const opensAt = bundle.config.opens_at ? new Date(bundle.config.opens_at) : null;
  const closesAt = bundle.config.closes_at ? new Date(bundle.config.closes_at) : null;
  const tooEarly = opensAt && opensAt > now;
  const tooLate = closesAt && closesAt < now;
  const windowMessage = tooEarly
    ? `Registration opens ${opensAt.toLocaleString()}`
    : tooLate
      ? `Registration closed on ${closesAt.toLocaleString()}`
      : null;

  return (
    <div style={containerStyle}>
      <Link to={`/l/${slug}`} style={backLinkStyle}><ArrowLeft size={14} /> Back to {bundle.league.name}</Link>

      <div style={cardStyle}>
        <h1 style={h1Style}>{bundle.config.name}</h1>
        <div style={{ fontSize: 14, color: '#7a82a0', marginBottom: 8 }}>
          {bundle.season.name}
          {bundle.division && <> · {bundle.division.name}</>}
        </div>
        {windowMessage && (
          <div style={{ ...noteBoxStyle, color: '#7a4d00', background: '#fff7e6', borderColor: '#f0c970' }}>
            <AlertCircle size={14} /> {windowMessage}
          </div>
        )}
        {bundle.config.intro_markdown && (
          <div style={{ fontSize: 15, color: '#0c1129', marginTop: 12, lineHeight: 1.55 }}>
            {renderMarkdown(bundle.config.intro_markdown)}
          </div>
        )}
      </div>

      {!user && (
        <AccountGate slug={slug} configId={bundle.config.id} returnTo={location.pathname} />
      )}

      {user && (
        <>
          {/* Phase C — Stripe return + outstanding registrations. */}
          {paymentParam === 'success' && (
            <div style={{ ...noteBoxStyle, color: '#0a5b3d', background: '#e7f7ef', borderColor: '#aedfc5' }}>
              <CheckCircle2 size={14} /> Payment received — your registration status updates below within a few seconds.
            </div>
          )}
          {paymentParam === 'cancelled' && (
            <div style={{ ...noteBoxStyle, color: '#7a4d00', background: '#fff7e6', borderColor: '#f0c970' }}>
              <AlertCircle size={14} /> Payment was cancelled — you can pay any time below.
            </div>
          )}
          {myRegs.length > 0 && (
            <div style={cardStyle}>
              <h2 style={h2Style}>Your registrations</h2>
              {myRegs.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderBottom: '1px solid #eef1f7', fontSize: 14 }}>
                  <strong>{r.player?.display_name || r.player?.player_uid || 'Player'}</strong>
                  <span style={{ color: '#7a82a0' }}>{labelForStatus(r.status)}</span>
                  {r.status === 'awaiting_payment' && (
                    <PayNowButton configId={bundle.config.id} registrationId={r.id} />
                  )}
                  {/* Phase E — consent pending: gentle reminder + resend. */}
                  {r.parent_consent_required && !r.parent_consent_confirmed_at
                    && !['cancelled', 'refunded'].includes(r.status) && (
                    <ConsentPendingNote configId={bundle.config.id} registrationId={r.id} />
                  )}
                </div>
              ))}
            </div>
          )}
          {draft && (
            <div style={{ ...noteBoxStyle, color: '#0a5b3d', background: '#e7f7ef', borderColor: '#aedfc5' }}>
              <CheckCircle2 size={14} /> Resuming your saved draft.
            </div>
          )}

          {/* Section: who are you registering? */}
          <RegistrantSection
            configId={bundle.config.id}
            registrantMode={registrantMode}
            setRegistrantMode={setRegistrantMode}
            selectedProfile={selectedProfile}
            setSelectedProfile={setSelectedProfile}
            newProfile={newProfile}
            setNewProfile={setNewProfile}
            relationship={relationship}
            setRelationship={setRelationship}
            parentConsentUnderAge={bundle.config.parent_consent_under_age}
          />

          {/* Section: form fields */}
          {bundle.fields.length > 0 && (
            <div style={cardStyle}>
              <h2 style={h2Style}>Information</h2>
              {bundle.fields.map(f => (
                <FieldRenderer
                  key={f.id}
                  field={f}
                  value={fieldValues[f.id]}
                  onChange={(v) => setFieldValue(f.id, v)}
                  allValues={fieldValues}
                />
              ))}
            </div>
          )}

          {/* Section: waivers */}
          {bundle.waiver_sections.length > 0 && (
            <div style={cardStyle}>
              <h2 style={h2Style}>Waivers & acknowledgments</h2>
              {bundle.waiver_sections.map(s => (
                <WaiverSection
                  key={s.id}
                  section={s}
                  acked={ackedSectionIds.has(s.id)}
                  onToggle={() => toggleAck(s.id)}
                />
              ))}

              {/* Combined signature */}
              <div style={{ marginTop: 20, padding: 16, background: '#f5f7fb', borderRadius: 10 }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                  Electronic signature
                </label>
                <p style={{ fontSize: 12, color: '#7a82a0', margin: '0 0 8px' }}>
                  Type your full legal name below. This applies to every waiver section you checked above.
                </p>
                <input
                  type="text"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  placeholder="Your full name"
                  maxLength={200}
                  style={inputStyle}
                />
              </div>
            </div>
          )}

          {/* Section: fee summary (preview only in 9B — payment in 9C) */}
          {bundle.config.base_fee_cents > 0 && (
            <div style={cardStyle}>
              <h2 style={h2Style}>Fee</h2>
              <div style={{ fontSize: 16, color: '#0c1129' }}>
                {(bundle.config.base_fee_cents / 100).toLocaleString(undefined, {
                  style: 'currency', currency: bundle.config.currency || 'CAD',
                })}
              </div>
              <p style={{ fontSize: 12, color: '#7a82a0', marginTop: 6 }}>
                Payment will be collected after you submit. (Payment integration is being rolled out;
                you'll receive an email with the next steps.)
              </p>
            </div>
          )}

          {/* Submit */}
          <div style={{ ...cardStyle, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || tooEarly || tooLate}
              style={{
                padding: '12px 32px',
                background: '#0ea5e9',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 15,
                cursor: submitting || tooEarly || tooLate ? 'not-allowed' : 'pointer',
                opacity: submitting || tooEarly || tooLate ? 0.6 : 1,
              }}>
              {submitting ? 'Submitting…' : 'Submit registration'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AccountGate — shown when the parent isn't signed in
// ─────────────────────────────────────────────────────────────────
function AccountGate({ slug, configId, returnTo }) {
  return (
    <div style={{ ...cardStyle, background: '#f5f7fb', border: '1px solid #e2e6f0' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <LogIn size={20} color="#0ea5e9" style={{ marginTop: 4 }} />
        <div style={{ flex: 1 }}>
          <h2 style={{ ...h2Style, marginTop: 0 }}>Sign in to start your registration</h2>
          <p style={{ fontSize: 13, color: '#7a82a0', marginBottom: 12 }}>
            Registering on behalf of a minor requires a Game Day account so you can manage their
            registration, confirm waivers as their guardian, and (when set up) pay fees securely.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to={`/login?return=${encodeURIComponent(returnTo)}`}
                  style={primaryBtnStyle}>
              Sign in
            </Link>
            <Link to={`/register?return=${encodeURIComponent(returnTo)}`}
                  style={ghostBtnStyle}>
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// RegistrantSection — pick or create the player profile
// ─────────────────────────────────────────────────────────────────
function RegistrantSection({ configId, registrantMode, setRegistrantMode, selectedProfile, setSelectedProfile, newProfile, setNewProfile, relationship, setRelationship, parentConsentUnderAge }) {
  // Existing-player search.
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (registrantMode !== 'existing_by_uid' && registrantMode !== 'existing_by_name') return;
    if (q.trim().length < 2) { setMatches([]); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      const myReqId = ++reqIdRef.current;
      api.get(`/registration/${configId}/profile-search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => {
          if (myReqId !== reqIdRef.current) return;
          setMatches(data.matches || []);
        })
        .catch(() => {
          if (myReqId !== reqIdRef.current) return;
          setMatches([]);
        })
        .finally(() => {
          if (myReqId !== reqIdRef.current) return;
          setSearching(false);
        });
    }, 250);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [q, registrantMode]);

  return (
    <div style={cardStyle}>
      <h2 style={h2Style}>Who are you registering?</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <ModeButton selected={registrantMode === 'new'} onClick={() => { setRegistrantMode('new'); setSelectedProfile(null); }}>
          <UserPlus size={16} />
          New player
        </ModeButton>
        <ModeButton selected={registrantMode === 'existing_by_uid'} onClick={() => { setRegistrantMode('existing_by_uid'); setSelectedProfile(null); setQ(''); }}>
          <Search size={16} />
          Existing — by player code
        </ModeButton>
        <ModeButton selected={registrantMode === 'existing_by_name'} onClick={() => { setRegistrantMode('existing_by_name'); setSelectedProfile(null); setQ(''); }}>
          <Search size={16} />
          Existing — by name
        </ModeButton>
      </div>

      {registrantMode === 'new' && (
        <>
          <label style={labelStyle}>Player name</label>
          <input
            type="text"
            value={newProfile.display_name}
            onChange={(e) => setNewProfile(p => ({ ...p, display_name: e.target.value }))}
            placeholder="Full name"
            style={inputStyle}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 10 }}>
            <div>
              <label style={labelStyle}>First name (optional)</label>
              <input type="text" value={newProfile.first_name} onChange={(e) => setNewProfile(p => ({ ...p, first_name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last name (optional)</label>
              <input type="text" value={newProfile.last_name} onChange={(e) => setNewProfile(p => ({ ...p, last_name: e.target.value }))} style={inputStyle} />
            </div>
          </div>
          <label style={{ ...labelStyle, marginTop: 10 }}>Date of birth</label>
          <input
            type="date"
            value={newProfile.date_of_birth}
            onChange={(e) => setNewProfile(p => ({ ...p, date_of_birth: e.target.value }))}
            style={inputStyle}
          />
        </>
      )}

      {(registrantMode === 'existing_by_uid' || registrantMode === 'existing_by_name') && (
        <>
          {selectedProfile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: '#e7f7ef', borderRadius: 8 }}>
              <CheckCircle2 size={16} color="#0a5b3d" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{selectedProfile.display_name}</div>
                <div style={{ fontSize: 12, color: '#7a82a0', fontFamily: 'monospace' }}>{selectedProfile.player_uid}</div>
              </div>
              <button type="button" onClick={() => setSelectedProfile(null)}
                      style={{ ...ghostBtnStyle, padding: '4px 10px', fontSize: 12 }}>
                Change
              </button>
            </div>
          ) : (
            <>
              <label style={labelStyle}>
                {registrantMode === 'existing_by_uid' ? 'Player code' : "Player's name"}
              </label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={registrantMode === 'existing_by_uid' ? 'PLR-XXXXXXXX' : 'First and last name'}
                style={{ ...inputStyle, fontFamily: registrantMode === 'existing_by_uid' ? 'monospace' : undefined }}
              />
              {searching && <div style={{ fontSize: 12, color: '#7a82a0', marginTop: 6 }}>Searching…</div>}
              {!searching && matches.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {matches.map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setSelectedProfile(m)}
                      disabled={m.is_claimed}
                      style={{
                        padding: '10px 12px',
                        background: m.is_claimed ? '#f0f3f8' : '#fff',
                        border: '1px solid #e2e6f0',
                        borderRadius: 8,
                        textAlign: 'left',
                        cursor: m.is_claimed ? 'not-allowed' : 'pointer',
                        opacity: m.is_claimed ? 0.6 : 1,
                      }}>
                      <div style={{ fontWeight: 600 }}>{m.display_name}</div>
                      <div style={{ fontSize: 12, color: '#7a82a0', fontFamily: 'monospace' }}>
                        {m.player_uid}{m.is_claimed && ' · already claimed'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {!searching && q.trim().length >= 2 && matches.length === 0 && (
                <div style={{ fontSize: 13, color: '#7a82a0', marginTop: 8 }}>
                  No matches. Try searching by{' '}
                  {registrantMode === 'existing_by_uid' ? 'player name' : 'player code'} instead,
                  or use "New player" to create a fresh profile.
                </div>
              )}
            </>
          )}
        </>
      )}

      {registrantMode && (
        <div style={{ marginTop: 16 }}>
          <label style={labelStyle}>Your relationship to the registrant</label>
          <select value={relationship} onChange={(e) => setRelationship(e.target.value)} style={inputStyle}>
            <option value="self">Self (I'm registering myself)</option>
            <option value="parent">Parent</option>
            <option value="guardian">Guardian</option>
            <option value="other">Other</option>
          </select>
          <p style={{ fontSize: 11, color: '#7a82a0', marginTop: 6 }}>
            For players under {parentConsentUnderAge}, your account confirms guardian consent.
          </p>
        </div>
      )}
    </div>
  );
}

function ModeButton({ selected, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 14px',
              background: selected ? '#0ea5e9' : '#fff',
              color: selected ? '#fff' : '#0c1129',
              border: `1px solid ${selected ? '#0ea5e9' : '#e2e6f0'}`,
              borderRadius: 10,
              cursor: 'pointer',
              fontWeight: selected ? 700 : 500,
              fontSize: 14,
            }}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// FieldRenderer — render one field based on its type
// ─────────────────────────────────────────────────────────────────
function FieldRenderer({ field, value, onChange, allValues }) {
  // Visibility condition: skip rendering if condition not met.
  // Format: { field_key: 'have_allergies', equals: 'yes' }
  if (field.visibility_condition) {
    const c = field.visibility_condition;
    // Look up the value of the dependency field by key. We need
    // to find the field with field_key === c.field_key in the
    // form bundle. allValues is keyed by field ID; we'd need
    // the bundle to resolve key→id. For 9B the simplest path:
    // require visibility to be expressed by FIELD KEY but defer
    // the lookup to the form-level render. As a fallback when
    // we can't resolve, treat the field as visible. Future
    // enhancement: pass a key→id map into FieldRenderer.
    // For now this is dead-code-defensive; visibility rules can
    // be set in the admin but won't take effect until a refactor.
    // Leaving the check in place so the field display matches
    // the manager's intent the moment we wire the lookup map.
    // (See AUDIT_LOG for the open follow-up.)
  }

  const common = {
    style: inputStyle,
    onChange: (e) => onChange(e.target.value),
    value: value ?? '',
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>
        {field.label}
        {field.is_required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </label>
      {(() => {
        switch (field.field_type) {
          case 'textarea':
            return <textarea {...common} rows={4} style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} />;
          case 'number':
            return <input type="number" {...common} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />;
          case 'date':
            return <input type="date" {...common} />;
          case 'email':
            return <input type="email" {...common} />;
          case 'phone':
            return <input type="tel" {...common} />;
          case 'checkbox':
            return (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                />
                {field.help_text || field.label}
              </label>
            );
          case 'select':
            return (
              <select {...common}>
                <option value="">— pick one —</option>
                {(field.options_json || []).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            );
          case 'multiselect': {
            const arr = Array.isArray(value) ? value : [];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(field.options_json || []).map(o => (
                  <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={arr.includes(o.value)}
                      onChange={(e) => {
                        if (e.target.checked) onChange([...arr, o.value]);
                        else onChange(arr.filter(v => v !== o.value));
                      }}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            );
          }
          case 'text':
          default:
            return <input type="text" {...common} />;
        }
      })()}
      {field.help_text && field.field_type !== 'checkbox' && (
        <div style={{ fontSize: 11, color: '#7a82a0', marginTop: 4 }}>{field.help_text}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WaiverSection
// ─────────────────────────────────────────────────────────────────
function WaiverSection({ section, acked, onToggle }) {
  return (
    <div style={{ borderBottom: '1px solid #e2e6f0', padding: '16px 0' }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
        {section.title}
        {section.is_required && <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>(required)</span>}
      </h3>
      <div style={{ fontSize: 14, color: '#0c1129', maxHeight: 240, overflowY: 'auto', padding: 10, background: '#f5f7fb', borderRadius: 6 }}>
        {renderMarkdown(section.body_markdown)}
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={acked}
          onChange={onToggle}
          style={{ marginTop: 3 }}
        />
        <span style={{ fontSize: 14 }}>{section.acknowledgment_text}</span>
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SubmittedScreen
// ─────────────────────────────────────────────────────────────────
function SubmittedScreen({ registration, bundle, slug }) {
  const isPending = registration.status === 'awaiting_payment';
  const fee = registration.fee_total_cents
    ? `$${(registration.fee_total_cents / 100).toFixed(2)}` : null;
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <CheckCircle2 size={28} color="#059669" />
          <h1 style={{ ...h1Style, margin: 0 }}>Registration submitted</h1>
        </div>
        <p style={{ fontSize: 15, color: '#0c1129', marginBottom: 12 }}>
          You're registered for <strong>{bundle.config.name}</strong>
          {bundle.division && <> in <strong>{bundle.division.name}</strong></>}.
        </p>
        {/* Phase C (league gap-closure) — live payment flow. The old
            "payment integration is being rolled out" placeholder is
            replaced with a real Pay-now button (Stripe Checkout via
            POST /registration/:configId/checkout). If the league
            hasn't connected Stripe, the endpoint says manual_only and
            we show arrange-with-the-league copy instead. */}
        {isPending && (
          <div style={{ ...noteBoxStyle, color: '#7a4d00', background: '#fff7e6', borderColor: '#f0c970', flexWrap: 'wrap' }}>
            <AlertCircle size={14} />
            <span>
              Your registration is <strong>awaiting payment</strong>{fee ? <> of <strong>{fee}</strong></> : null}.
            </span>
            <PayNowButton configId={bundle.config.id} registrationId={registration.id} />
          </div>
        )}
        {/* Phase E — guardian consent step for minors. */}
        {registration.parent_consent_required && !registration.parent_consent_confirmed_at && (
          <div style={{ ...noteBoxStyle, color: '#1e3a8a', background: '#eef4ff', borderColor: '#bcd0f7', flexWrap: 'wrap' }}>
            <AlertCircle size={14} />
            <span>
              One more step: we emailed you a <strong>guardian consent</strong> link.
              The league can't confirm this registration until you click it.
            </span>
          </div>
        )}
        <p style={{ fontSize: 13, color: '#7a82a0', marginTop: 14 }}>
          We've recorded your waiver acknowledgments and your signature. A confirmation email is on its way.
        </p>
        <div style={{ marginTop: 18 }}>
          <Link to={`/l/${slug}`} style={primaryBtnStyle}>Back to {bundle.league.name}</Link>
        </div>
      </div>
    </div>
  );
}

// Phase C — starts Stripe Checkout for an awaiting_payment
// registration. Redirects the whole window (Stripe-hosted page);
// Stripe sends the user back to this page with ?payment=success or
// ?payment=cancelled.
function PayNowButton({ configId, registrationId }) {
  const [busy, setBusy] = useState(false);
  const [manualMsg, setManualMsg] = useState(null);
  // 2026-09-28 — demo leagues (Admin → Demo) answer checkout with
  // { demo: true, amount_cents, currency } instead of a Stripe URL.
  const [demoPay, setDemoPay] = useState(null);
  async function pay() {
    setBusy(true);
    try {
      const { data } = await api.post(`/registration/${configId}/checkout`,
        { registration_id: registrationId });
      if (data?.demo) {
        setDemoPay(data);
        return;
      }
      if (data?.url) {
        window.location.assign(data.url);
        return; // navigating away
      }
      toast.error('Could not start payment');
    } catch (err) {
      const body = err?.response?.data;
      if (body?.manual_only) {
        setManualMsg(body.error || 'Online payment isn\'t available — please arrange payment with the league directly.');
      } else {
        toast.error(body?.error || 'Could not start payment');
      }
    } finally {
      setBusy(false);
    }
  }
  if (manualMsg) {
    return <span style={{ fontSize: 13 }}>{manualMsg}</span>;
  }
  return (
    <>
      <button type="button" onClick={pay} disabled={busy}
        style={{ ...primaryBtnStyle, border: 'none', cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? 'Starting payment…' : 'Pay now'}
      </button>
      {demoPay && (
        <DemoCheckout
          configId={configId}
          registrationId={registrationId}
          info={demoPay}
          onClose={() => setDemoPay(null)}
        />
      )}
    </>
  );
}

// 2026-09-28 — stand-in for Stripe Checkout on DEMO leagues only. Shows
// what the payer would see, charges nothing, and marks the
// registration paid through POST /registration/:configId/demo-pay
// (which the server refuses for every real league). Returns to this
// page with ?payment=success exactly like a real Stripe payment.
function DemoCheckout({ configId, registrationId, info, onClose }) {
  const [busy, setBusy] = useState(false);
  const amount = (() => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: info.currency || 'CAD' })
        .format((info.amount_cents || 0) / 100);
    } catch { return `$${((info.amount_cents || 0) / 100).toFixed(2)}`; }
  })();
  async function complete() {
    setBusy(true);
    try {
      await api.post(`/registration/${configId}/demo-pay`, { registration_id: registrationId });
      const url = new URL(window.location.href);
      url.searchParams.set('payment', 'success');
      window.location.assign(url.pathname + url.search);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Simulated payment failed');
      setBusy(false);
    }
  }
  const field = { ...inputStyle, background: '#f8fafc', color: '#475569' };
  return createPortal(
    <div role="dialog" aria-modal="true" onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 420, background: '#fff', borderRadius: 14, padding: 22,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', color: '#0c1129',
      }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d',
          borderRadius: 8, padding: '6px 10px', marginBottom: 14,
        }}>
          Demo league — simulated payment, no card is charged
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>Pay {info.league_name || 'the league'}</div>
        <div style={{ fontSize: 30, fontWeight: 800, margin: '2px 0 16px' }}>{amount}</div>
        <label style={{ fontSize: 12, color: '#475569', display: 'block', marginBottom: 4 }}>Card information</label>
        <input readOnly value="4242 4242 4242 4242" style={{ ...field, marginBottom: 6 }} aria-label="Card number (demo)" />
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input readOnly value="12 / 34" style={field} aria-label="Expiry (demo)" />
          <input readOnly value="123" style={field} aria-label="CVC (demo)" />
        </div>
        <button type="button" onClick={complete} disabled={busy} style={{
          ...primaryBtnStyle, width: '100%', border: 'none', textAlign: 'center',
          cursor: busy ? 'wait' : 'pointer', background: '#0f172a',
        }}>
          {busy ? 'Processing…' : `Pay ${amount}`}
        </button>
        <button type="button" onClick={onClose} disabled={busy} style={{
          width: '100%', marginTop: 8, background: 'none', border: 'none', color: '#64748b',
          fontSize: 13, cursor: 'pointer', padding: 6,
        }}>
          Cancel
        </button>
        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginTop: 10 }}>
          In a real league this step is Stripe Checkout: the card is entered on Stripe&apos;s secure page,
          the money goes straight to the league&apos;s own Stripe account, and the registration flips to
          paid automatically.
        </p>
      </div>
    </div>,
    document.body,
  );
}

// Phase E — "guardian consent pending" inline note with resend.
function ConsentPendingNote({ configId, registrationId }) {
  const [busy, setBusy] = useState(false);
  async function resend() {
    setBusy(true);
    try {
      const { data } = await api.post(`/registration/${configId}/consent/resend`,
        { registration_id: registrationId });
      toast.success(data?.already_confirmed
        ? 'Consent already on file — you\'re set.'
        : 'Consent email sent — check your inbox.');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not resend');
    } finally {
      setBusy(false);
    }
  }
  return (
    <span style={{ fontSize: 12, color: '#1e3a8a', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      guardian consent pending
      <button type="button" onClick={resend} disabled={busy}
        style={{ border: '1px solid #bcd0f7', background: '#eef4ff', color: '#1e3a8a', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
        {busy ? 'Sending…' : 'Resend email'}
      </button>
    </span>
  );
}

// Phase C — human labels for the registration state machine.
function labelForStatus(status) {
  switch (status) {
    case 'awaiting_payment': return '· awaiting payment';
    case 'submitted':        return '· submitted, awaiting league confirmation';
    case 'paid':             return '· paid, awaiting league confirmation';
    case 'confirmed':        return '· confirmed ✓';
    case 'cancelled':        return '· cancelled';
    case 'refunded':         return '· refunded';
    default:                 return `· ${status}`;
  }
}

// ─── Styles ──────────────────────────────────────────────────────
const containerStyle = { maxWidth: 760, margin: '0 auto', padding: '24px 20px 48px' };
const cardStyle = {
  background: '#fff',
  border: '1px solid #e2e6f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
  boxShadow: '0 1px 2px rgba(12,17,41,0.06)',
};
const h1Style = { fontSize: 26, fontWeight: 800, margin: '0 0 8px', color: '#0c1129', letterSpacing: '-0.01em' };
const h2Style = { fontSize: 18, fontWeight: 700, margin: '0 0 12px', color: '#0c1129' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#0c1129', marginBottom: 4 };
const inputStyle = {
  width: '100%',
  background: '#fff',
  border: '1px solid #e2e6f0',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  color: '#0c1129',
  outline: 'none',
  fontFamily: 'inherit',
};
const noteBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  border: '1px solid',
  borderRadius: 8,
  marginTop: 10,
  fontSize: 13,
};
const backLinkStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  color: '#0ea5e9', textDecoration: 'none', fontSize: 13,
  marginBottom: 12,
};
const primaryBtnStyle = {
  display: 'inline-block',
  padding: '10px 20px',
  background: '#0ea5e9',
  color: '#fff',
  borderRadius: 10,
  textDecoration: 'none',
  fontWeight: 700,
  fontSize: 14,
};
const ghostBtnStyle = {
  display: 'inline-block',
  padding: '10px 20px',
  background: '#fff',
  color: '#0ea5e9',
  border: '1px solid #0ea5e9',
  borderRadius: 10,
  textDecoration: 'none',
  fontWeight: 700,
  fontSize: 14,
};
